import clsx from "clsx";
import React, { MouseEventHandler, useCallback } from "react";
import { ButtonAsButtonProps } from "../Button/Button";
import { Icon } from "../Icon/Icon";
import { isPresent } from "../utils/checks";
import styles from "./PromoBadges.module.scss";

export type PromoBadgeContent = {
  code: string;
  title: string;
};

export type PromoBadgesProps = {
  className?: string;
  promos: PromoBadgeContent[];
  variant?: "normal" | "small";
  inverted?: boolean;
  onPromoBadgeClicked?: (promoCode: string) => void;
};

export const PromoBadges: React.FC<PromoBadgesProps> = ({
  className,
  promos,
  onPromoBadgeClicked,
  inverted = false,
  variant = "normal"
}) => {
  if (promos.length == 0) return null;

  return (
    <div
      className={clsx(styles.container, className, {
        [styles.containerSmall]: variant == "small"
      })}
    >
      <Icon
        name="promo"
        className={clsx(styles.promoIcon, {
          [styles.inverted]: inverted
        })}
        size="1x"
        padding={false}
      />
      {promos.map((p) => (
        <PromoBadge
          key={p.code}
          promo={p}
          onPromoBadgeClicked={onPromoBadgeClicked}
          inverted={inverted}
        />
      ))}
    </div>
  );
};

type PromoBadgeProps = {
  promo: PromoBadgeContent;
  onPromoBadgeClicked?: (promoCode: string) => void;
  inverted: boolean;
};

const PromoBadge: React.FC<PromoBadgeProps> = ({
  promo,
  onPromoBadgeClicked,
  inverted
}) => {
  const _onPromoBadgeClicked = useCallback(
    () => onPromoBadgeClicked?.(promo.code),
    [onPromoBadgeClicked, promo.code]
  );
  return (
    <WrapWithLink
      key={promo.code}
      className={clsx(styles.item, {
        [styles.inverted]: inverted,
        [styles.asLink]: isPresent(onPromoBadgeClicked)
      })}
      onClick={onPromoBadgeClicked ? _onPromoBadgeClicked : undefined}
      dataContentPiece={`promo_badge_click__${promo.code}`}
    >
      {promo.title}
    </WrapWithLink>
  );
};

const WrapWithLink: React.FC<
  React.PropsWithChildren<
    Omit<
      React.DetailedHTMLProps<
        React.HTMLAttributes<HTMLDivElement>,
        HTMLDivElement
      >,
      "onClick"
    > & {
      onClick?: ButtonAsButtonProps["onClick"];
      dataContentPiece?: string;
    }
  >
> = ({ onClick, children, dataContentPiece, ...otherProps }) => {
  const onKeyDown = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (e: any) => {
      if (onClick && (e.code == "Space" || e.code == "Enter")) onClick(e);
    },
    [onClick]
  );

  if (isPresent(onClick))
    return (
      <div
        {...otherProps}
        onClick={onClick as unknown as MouseEventHandler<HTMLDivElement>}
        onKeyDown={onKeyDown}
        tabIndex={0}
        role="button"
        aria-pressed="false"
        style={{ cursor: "pointer" }}
        data-content-piece={dataContentPiece}
      >
        {children}
      </div>
    );
  else return <div {...otherProps}>{children}</div>;
};
